import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Grid, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCompletedEnrollment, getStudentList, getTotalAttendenceList } from '../../Utils/apiService';
import { getUserDetails } from '../../storageService';
import KenLoader from '../../GlobalComponents/KenLoader';
import KenSelect from '../../GlobalComponents/KenSelect';
import { makeStyles } from '@mui/styles';

interface Course {
    CourseCode: string;
    CourseName: string;
    credit: string;
    courseOfferingSectionId: string;
    term: string;
    courseOfferingId: string;
}

interface StudentCounts {
    [courseOfferingId: string]: number;
}
interface AttendanceCounts {
    [courseOfferingId: string]: number;
}
const useStyles = makeStyles(() => ({
    formControl: {
        width: "300px",
    },
    tableTitle: {
        color: '#193389 !important',
        fontSize: '16px',
        fontWeight: '500 !important',
        border: '#EDEEF1'

    },
    tableBody: {
        color: '#585A64 !important',
        fontSize: '16px',
        fontWeight: '400 !important',
        border: "none !important"
    }
}));
const FacultyCourses: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const classes = useStyles();

    const [loading, setLoading] = useState<boolean>(true);
    const [contactId, setContactId] = useState<string | null>(null);
    const [courses, setCourses] = useState<Course[]>([]);
    const [courseOfferingIds, setCourseOfferingIds] = useState<string[]>([]);
    const [studentsCount, setStudentsCount] = useState<StudentCounts>({});
    const [selectedProgram, setSelectedProgram] = useState('');
    const [attendanceCounts, setAttendanceCounts] = useState<AttendanceCounts>({});


    // Get user details
    useEffect(() => {
        const userDetails = getUserDetails();
        if (userDetails?.ContactId) {
            setContactId(userDetails.ContactId);
        }
    }, []);

    // Fetch course details
    const fetchCourseDetails = useCallback(async () => {
        if (!contactId) return;

        try {
            setLoading(true);
            const courseData = await getCompletedEnrollment(contactId);
            const allCourses = [...(courseData.mandatoryCourses || []), ...(courseData.electiveCourses || [])];
            setCourses(allCourses);
            setCourseOfferingIds(allCourses.map(course => course.courseOfferingId));
        } catch (error) {
            console.error("Error fetching courses:", error);
        } finally {
            setLoading(false);
        }
    }, [contactId]);

    // Fetch students data
    const fetchStudentsData = useCallback(async () => {
        if (!courseOfferingIds.length || !contactId) return;

        try {
            setLoading(true);
            const courseStudentCounts: StudentCounts = {};

            for (const courseOfferingId of courseOfferingIds) {
                const studentDataTable = await getStudentList(courseOfferingId);
                courseStudentCounts[courseOfferingId] = studentDataTable.data.studentsList.length;
            }

            setStudentsCount(courseStudentCounts);
        } catch (error) {
            console.error("Error fetching student data:", error);
        } finally {
            setLoading(false);
        }
    }, [courseOfferingIds, contactId]);

    const fetchAttendenceData = useCallback(async () => {
        if (!courseOfferingIds.length || !contactId) return;

        try {
            setLoading(true);
            const courseAttendenceCounts: AttendanceCounts = {};

            for (const courseOfferingId of courseOfferingIds) {
                const AttendenceData = await getTotalAttendenceList(courseOfferingId);
                const roundedPercentage = Math.round(AttendenceData?.AttendancePercentage || 0);
                courseAttendenceCounts[courseOfferingId] = roundedPercentage;
            }

            setAttendanceCounts(courseAttendenceCounts);
        } catch (error) {
            console.error("Error fetching student data:", error);
        } finally {
            setLoading(false);
        }
    }, [courseOfferingIds]);

    // Run API calls on `contactId` change
    useEffect(() => {
        if (contactId) fetchCourseDetails();
    }, [contactId, fetchCourseDetails]);

    useEffect(() => {
        if (courseOfferingIds.length) fetchStudentsData();
    }, [courseOfferingIds, fetchStudentsData]);
    useEffect(() => {
        if (courseOfferingIds.length) fetchAttendenceData();
    }, [courseOfferingIds, fetchAttendenceData]);

    return (
        <Grid sx={{ height: '35vh !important' }}>
            <Grid sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, paddingLeft: 2, }}>
                <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>Attendance</Typography>
                <Select
                    value={selectedProgram}
                    onChange={(e) => setSelectedProgram(e.target.value)}
                    displayEmpty
                    sx={{
                        width: "250px",
                        height: "48px",
                        borderRadius: "8px",
                        border: "1px solid #B2B3B7",
                        backgroundColor: "white",
                    }}
                >
                    <MenuItem value="" disabled >
                        <Typography style={{ color: 'grey', textAlign: 'left', alignItems: 'center' }}>Select Program</Typography>
                    </MenuItem>
                    {courses.map((course) => (
                        <MenuItem key={course.courseOfferingId} value={course.term}>
                            {course.term}
                        </MenuItem>
                    ))}
                </Select>
            </Grid>


            <TableContainer component={Paper} sx={{
                boxShadow: 'none !important', px: 2, height: 'calc(40vh) !important',
                overflowY: "scroll",
                "&::-webkit-scrollbar": {
                    display: "none",
                }
            }}>
                <Table
                    sx={{
                        border: "2px solid #F6F8FB",
                        borderRadius: "8px",
                        borderCollapse: "separate",

                    }}
                >
                    <TableHead>
                        <TableRow sx={{ backgroundColor: "#F5F7FA" }}>
                            <TableCell className={classes.tableTitle}>Program</TableCell>
                            <TableCell className={classes.tableTitle}>Batch</TableCell>
                            <TableCell className={classes.tableTitle}>No of Students</TableCell>
                            <TableCell className={classes.tableTitle}>Total Attendance</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    <KenLoader />
                                </TableCell>
                            </TableRow>
                        ) : (
                            (selectedProgram ? courses.filter(course => course.term === selectedProgram) : courses).length > 0 ? (
                                (selectedProgram ? courses.filter(course => course.term === selectedProgram) : courses).map((course) => (
                                    <TableRow key={course.courseOfferingId}>
                                        <TableCell className={classes.tableBody}>{course.CourseName}</TableCell>
                                        <TableCell className={classes.tableBody}>{course.term}</TableCell>
                                        <TableCell className={classes.tableBody}>{studentsCount[course.courseOfferingId] || 0}</TableCell>
                                        <TableCell className={classes.tableBody}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {(() => {
                                                    const attendance: number = attendanceCounts[course.courseOfferingId] || 0;
                                                    const getColor = (value: number) => {
                                                        if (value === 100) return "green";
                                                        if (value < 50) return "rgb(192, 43, 43)";
                                                        return "#023E8A";
                                                    };

                                                    return (
                                                        <>
                                                            <CircularProgress
                                                                variant="determinate"
                                                                size={24}
                                                                thickness={4}
                                                                sx={{
                                                                    "& .MuiCircularProgress-circle": { stroke: getColor(attendance) },
                                                                    "& .MuiCircularProgress-circleTrack": { stroke: "white" }
                                                                }}
                                                                value={attendance}
                                                            />
                                                            <Typography sx={{ color: getColor(attendance), fontWeight: 500, fontSize: '18px' }}>
                                                                {attendance}%
                                                            </Typography>
                                                        </>
                                                    );
                                                })()}
                                            </Box>
                                        </TableCell>


                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        <Typography>No Program Available.</Typography>
                                    </TableCell>
                                </TableRow>
                            )
                        )}
                    </TableBody>
                </Table>
            </TableContainer>


        </Grid>
    );
};

export default FacultyCourses;
